import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Alert, Badge, Button, Card, Col, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';

import {
  backgroundGrey1,
  bootstrapSecondary,
  bootstrapPrimary,
  bootstrapDanger,
} from 'lib/css/colors';
import {
  CATALOG_IMPORT_WORKFLOW_MARK_COMPLETE,
  CATALOG_IMPORT_WORKFLOW_START,
  CATALOG_IMPORT_WORKFLOW_STATUS,
  CATALOG_IMPORT_WORKFLOW_RETRY_STEP,
} from 'lib/networking/endpoints';
import {
  CatalogImportStepStatus,
  CatalogImportStepStatusConfig,
  CatalogImportWorkflowStatus,
  CatalogImportWorkflowStatusConfig,
} from 'lib/enums/CatalogImportWorkflowEnums';
import { titleCase } from 'lib/utils/StringProcess';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const CatalogImportWorkflow = ({ globalVendorId }) => {
  const [workflowStatus, setWorkflowStatus] = useState(null);

  // Fetch workflow status
  const {
    data: statusData,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useGet(
    CATALOG_IMPORT_WORKFLOW_STATUS,
    {
      global_vendor_id: globalVendorId,
    },
    !globalVendorId,
  );

  // Start workflow
  const { postData: startWorkflow, loading: startingWorkflow } = usePost(
    CATALOG_IMPORT_WORKFLOW_START,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Catalog import workflow started successfully',
      });
      refetchStatus();
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to start workflow: ${error?.response?.data?.message || 'Unknown error'}`,
      }),
  );

  // Mark step complete
  const { postData: markStepComplete, loading: markingComplete } = usePost(
    CATALOG_IMPORT_WORKFLOW_MARK_COMPLETE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Step marked as complete successfully',
      });
      refetchStatus();
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to mark step as complete: ${
          error?.response?.data?.message || 'Unknown error'
        }`,
      }),
  );

  // Retry step
  const { postData: retryStep, loading: retryingStep } = usePost(
    CATALOG_IMPORT_WORKFLOW_RETRY_STEP,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Step retry initiated successfully',
      });
      refetchStatus();
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to retry step: ${error?.response?.data?.message || 'Unknown error'}`,
      }),
  );

  useEffect(() => {
    if (statusData) {
      setWorkflowStatus(statusData);
    }
  }, [statusData]);

  const getStatusBadge = status => {
    const config = CatalogImportStepStatusConfig[status] || { variant: 'secondary', text: status };
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getOverallStatusAlert = () => {
    if (!workflowStatus) return null;

    const { overall_status } = workflowStatus;
    const config = CatalogImportWorkflowStatusConfig[overall_status] || {
      variant: 'secondary',
      text: overall_status,
    };
    return <Alert variant={config.variant}>{config.text}</Alert>;
  };

  const handleStartWorkflow = () => {
    Swal.fire({
      title: 'Start Catalog Import Workflow?',
      text: 'This will start all the import processing steps for this vendor catalog.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Start Workflow',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        startWorkflow({
          global_vendor_id: globalVendorId,
          reset_existing: false,
        });
      }
    });
  };

  const handleMarkStepComplete = step => {
    Swal.fire({
      title: 'Mark Step as Complete?',
      text:
        `This will mark "${step.step_display_name}" as completed.` +
        ` This action cannot be undone.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Mark Complete',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        markStepComplete({
          global_vendor_id: globalVendorId,
          step_id: step.step_id,
          statistics_metadata: step.statistics_metadata || {},
        });
      }
    });
  };

  const handleRetryStep = step => {
    Swal.fire({
      title: 'Retry Step?',
      text:
        `This will retry "${step.step_display_name}" and reset all subsequent steps to pending.` +
        ` This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Retry Step',
      cancelButtonText: 'Cancel',
      confirmButtonColor: bootstrapDanger,
    }).then(result => {
      if (result.isConfirmed) {
        retryStep({
          global_vendor_id: globalVendorId,
          step_id: step.step_id,
        });
      }
    });
  };

  const formatProcessingTime = seconds => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const renderStatisticsMetadata = metadata => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return <span className="text-muted">No statistics available</span>;
    }

    return (
      <div className={css(styles.statisticsContainer)}>
        {Object.entries(metadata).map(([key, value]) => (
          <div key={key} className={css(styles.statisticItem)}>
            <div className={css(styles.statisticDescription)}>
              {titleCase(key.replace(/_/g, ' '))}
            </div>
            <div className={css(styles.statisticValue)}>
              {typeof value === 'number' ? `${value.toFixed(2)}%` : value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStepActions = step => {
    const actions = [];

    // Show complete button for manual steps that are in progress
    if (step.is_manual_step && step.status === CatalogImportStepStatus.IN_PROGRESS) {
      actions.push(
        <Button
          key="complete"
          variant="success"
          size="sm"
          onClick={() => handleMarkStepComplete(step)}
          disabled={markingComplete}
          className="mr-1"
        >
          {markingComplete ? 'Marking...' : 'Mark Complete'}
        </Button>,
      );
    }

    // Show retry button for retryable steps that are not pending
    if (step.can_retry && step.status !== CatalogImportStepStatus.PENDING) {
      actions.push(
        <Button
          key="retry"
          variant="warning"
          size="sm"
          onClick={() => handleRetryStep(step)}
          disabled={retryingStep}
          className="mr-1"
        >
          {retryingStep ? 'Retrying...' : 'Retry'}
        </Button>,
      );
    }

    return actions.length > 0 ? <div>{actions}</div> : <span className="text-muted">-</span>;
  };

  if (statusLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading workflow status...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">DP Partner Catalog Import Workflow</h5>
            <div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleStartWorkflow}
                disabled={
                  startingWorkflow ||
                  workflowStatus?.overall_status === CatalogImportWorkflowStatus.IN_PROGRESS
                }
                className="mr-2"
              >
                {startingWorkflow ? 'Starting...' : 'Start Workflow'}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => refetchStatus()}
                disabled={statusLoading}
                className="mr-2"
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {getOverallStatusAlert()}

          {workflowStatus?.overall_status === CatalogImportWorkflowStatus.IN_PROGRESS && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Workflow Progress</span>
              </div>
              <ProgressBar
                variant="info"
                animated
                now={
                  workflowStatus.steps.length > 0
                    ? (workflowStatus.steps.filter(
                        step => step.status === CatalogImportStepStatus.COMPLETED,
                      ).length /
                        workflowStatus.steps.length) *
                      100
                    : 0
                }
              />
            </div>
          )}

          {!workflowStatus?.workflow_initialized ? (
            <Alert variant="info">
              Workflow not initialized for this catalog. Click Start Workflow to begin.
            </Alert>
          ) : (
            <div>
              <Row className="mb-3">
                <Col>
                  <h6>Workflow Steps</h6>
                </Col>
              </Row>

              <Table responsive striped>
                <thead>
                  <tr>
                    <th className={`text-nowrap ${css(styles.stepHeader)}`}>Step</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Processing Time</th>
                    <th>Statistics</th>
                    <th>Actions</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {workflowStatus.steps.map(step => (
                    <tr key={step.id}>
                      <td>
                        <div>
                          <strong>{step.step_display_name}</strong>
                          <br />
                          <small className="text-muted">
                            {step.is_manual_step && (
                              <Badge variant="info" className="ml-1">
                                Manual
                              </Badge>
                            )}
                          </small>
                        </div>
                      </td>
                      <td>{getStatusBadge(step.status)}</td>
                      <td>
                        {step.started_at ? (
                          new Date(step.started_at).toLocaleString()
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{formatProcessingTime(step.processing_time_seconds)}</td>
                      <td>{renderStatisticsMetadata(step.statistics_metadata)}</td>
                      <td>{renderStepActions(step)}</td>
                      <td>
                        {step.error_message ? (
                          <div className="text-danger">
                            <small>{step.error_message}</small>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

const styles = StyleSheet.create({
  stepHeader: {
    width: '200px',
    minWidth: '200px',
  },
  statisticsContainer: {
    fontSize: '12px',
    padding: '4px',
  },
  statisticCategory: {
    marginBottom: '8px',
    padding: '4px',
    borderRadius: '4px',
    backgroundColor: backgroundGrey1,
  },
  statisticDescription: {
    color: bootstrapSecondary,
    marginBottom: '4px',
    fontWeight: '500',
  },
  statisticItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '2px',
  },
  statisticValue: {
    fontWeight: 'bold',
    color: bootstrapPrimary,
  },
});

CatalogImportWorkflow.propTypes = {
  globalVendorId: PropTypes.number.isRequired,
};

export default CatalogImportWorkflow;
